import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { X } from 'lucide-react';
import { useProfileInstructors } from '@/hooks/useProfileInstructors';
import { useToast } from '@/hooks/use-toast';
import { InstructorDisplay } from '@/components/ui/InstructorDisplay';
import { useI18n } from '@/contexts/I18nContext';

// Import the Instructor type from the hook
type Instructor = Awaited<ReturnType<typeof useProfileInstructors>>['instructors'][0];

interface EditInstructorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
}

export const EditInstructorsModal: React.FC<EditInstructorsModalProps> = ({
  isOpen,
  onClose,
  profileId
}) => {
  const { t } = useI18n();
  const { instructors, loading, addInstructor, removeInstructor, refetch } = useProfileInstructors(profileId);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  const handleClose = () => {
    onClose();
    // Refetch to ensure parent component has latest data
    refetch();
  };

  const handleAddInstructor = async (instructor: any) => {
    if (!instructor) return;

    try {
      if (typeof instructor === 'object' && instructor.id) {
        // sOSSiety user instructor
        await addInstructor(instructor.id);
      } else {
        // Manual instructor
        await addInstructor(undefined, instructor);
      }

      toast({
        title: t('profile.instructor_added'),
        description: t('profile.instructor_added_description'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('profile.error_adding_instructor'),
        variant: "destructive",
      });
    }
  };

  const handleRemoveInstructor = async (instructor: Instructor) => {
    try {
      if (instructor.is_manual) {
        await removeInstructor(undefined, instructor.manual_name);
      } else {
        await removeInstructor(instructor.instructor_user_id);
      }

      toast({
        title: t('profile.instructor_removed'),
        description: t('profile.instructor_removed_description'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('profile.error_removing_instructor'),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-black font-sans">{t('profile.edit_instructors')}</DialogTitle>
          <DialogDescription className="text-gray-600 text-sm">
            {t('profile.edit_instructors_description')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Add new instructor */}
          <div>
            <label className="block text-xs font-medium mb-1 text-black font-sans">
              {t('profile.add_instructor')}
            </label>
            <InstructorAutocomplete
              value={null}
              onChange={handleAddInstructor}
              placeholder="Type a name or a username"
              className="w-full"
              containerClassName="bg-white"
            />
          </div>

          {/* Current instructors list */}
          <div>
            <label className="block text-xs font-medium mb-2 text-black font-sans">
              My Instructors ({instructors.length})
            </label>
            
            {loading ? (
              <div className="text-sm text-gray-500">Loading instructors...</div>
            ) : instructors.length === 0 ? (
              <div className="text-sm text-gray-500">
                No instructors added yet. Add your first instructor above.
              </div>
            ) : (
              <div className="space-y-2">
                {instructors.map((instructor) => (
                  <div key={instructor.id} className="flex items-center justify-between p-3 rounded-lg">
                    <InstructorDisplay instructor={instructor} size="md" />
                    
                    <button
                      onClick={() => handleRemoveInstructor(instructor)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove instructor"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            className="text-black border-black hover:bg-gray-100 rounded-full"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 